import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface FloorPlanZone {
  id: string;
  establishment_id: string | null;
  name: string;
  color: string;
  order_index: number;
  grid_cols: number;
  grid_rows: number;
  is_default: boolean;
  layout_row: number;
  layout_col: number;
  canvas_width_px?: number | null;
  canvas_height_px?: number | null;
  background_color?: string | null;
  all_view_pos_x_px?: number | null;
  all_view_pos_y_px?: number | null;
  all_view_rotation?: number | null;
  created_at: string;
  updated_at: string;
}

interface UseFloorPlanZonesOptions {
  establishmentId?: string;
}

export const useFloorPlanZones = (options: UseFloorPlanZonesOptions = {}) => {
  const { establishmentId } = options;
  const queryClient = useQueryClient();

  const {
    data: zones = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["floor-plan-zones", establishmentId],
    queryFn: async () => {
      let query = supabase
        .from("floor_plan_zones")
        .select("*")
        .order("order_index", { ascending: true });

      if (establishmentId) {
        query = query.eq("establishment_id", establishmentId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as FloorPlanZone[];
    },
    enabled: !!establishmentId,
  });

  const createZoneMutation = useMutation({
    mutationFn: async (zone: Partial<FloorPlanZone>) => {
      const { data, error } = await supabase
        .from("floor_plan_zones")
        .insert({
          name: zone.name || "Nouvelle zone",
          color: zone.color || "#6366f1",
          order_index: zones.length,
          grid_cols: zone.grid_cols || 12,
          grid_rows: zone.grid_rows || 8,
          establishment_id: establishmentId,
        })
        .select()
        .single();

      if (error) throw error;
      return data as FloorPlanZone;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["floor-plan-zones", establishmentId] });
      toast.success("Zone créée avec succès");
    },
    onError: (error) => {
      console.error("Error creating zone:", error);
      toast.error("Erreur lors de la création de la zone");
    },
  });

  const updateZoneMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<FloorPlanZone> & { id: string }) => {
      const { error } = await supabase
        .from("floor_plan_zones")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["floor-plan-zones", establishmentId] });
      toast.success("Zone mise à jour");
    },
    onError: (error) => {
      console.error("Error updating zone:", error);
      toast.error("Erreur lors de la mise à jour de la zone");
    },
  });

  const deleteZoneMutation = useMutation({
    mutationFn: async (zoneId: string) => {
      const { error } = await supabase
        .from("floor_plan_zones")
        .delete()
        .eq("id", zoneId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["floor-plan-zones", establishmentId] });
      queryClient.invalidateQueries({ queryKey: ["floor-plan-positions", establishmentId] });
      toast.success("Zone supprimée");
    },
    onError: (error) => {
      console.error("Error deleting zone:", error);
      toast.error("Erreur lors de la suppression de la zone");
    },
  });

  const reorderZonesMutation = useMutation({
    mutationFn: async (reorderedZones: FloorPlanZone[]) => {
      // Update each zone's order_index
      const updates = reorderedZones.map((zone, index) =>
        supabase
          .from("floor_plan_zones")
          .update({ order_index: index })
          .eq("id", zone.id)
      );
      
      const results = await Promise.all(updates);
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        throw errors[0].error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["floor-plan-zones", establishmentId] });
      toast.success("Ordre des zones mis à jour");
    },
    onError: (error) => {
      console.error("Error reordering zones:", error);
      toast.error("Erreur lors de la réorganisation des zones");
    },
  });

  const setDefaultZoneMutation = useMutation({
    mutationFn: async (zoneId: string | null) => {
      // First, unset any existing default for this establishment
      const { error: unsetError } = await supabase
        .from("floor_plan_zones")
        .update({ is_default: false })
        .eq("establishment_id", establishmentId)
        .eq("is_default", true);

      if (unsetError) throw unsetError;

      // If zoneId is provided, set it as default
      if (zoneId) {
        const { error: setError } = await supabase
          .from("floor_plan_zones")
          .update({ is_default: true })
          .eq("id", zoneId);

        if (setError) throw setError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["floor-plan-zones", establishmentId] });
      toast.success("Zone par défaut mise à jour");
    },
    onError: (error) => {
      console.error("Error setting default zone:", error);
      toast.error("Erreur lors de la définition de la zone par défaut");
    },
  });

  const updateZoneLayoutMutation = useMutation({
    mutationFn: async ({ id, row, col }: { id: string; row: number; col: number }) => {
      const { error } = await supabase
        .from("floor_plan_zones")
        .update({ layout_row: row, layout_col: col })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["floor-plan-zones", establishmentId] });
    },
    onError: (error) => {
      console.error("Error updating zone layout:", error);
      toast.error("Erreur lors de la mise à jour de la position");
    },
  });

  const createZone = useCallback(
    (zone?: Partial<FloorPlanZone>) => createZoneMutation.mutateAsync(zone || {}),
    [createZoneMutation]
  );

  const updateZone = useCallback(
    (id: string, updates: Partial<FloorPlanZone>) =>
      updateZoneMutation.mutateAsync({ id, ...updates }),
    [updateZoneMutation]
  );

  const deleteZone = useCallback(
    (id: string) => deleteZoneMutation.mutateAsync(id),
    [deleteZoneMutation]
  );

  const reorderZones = useCallback(
    (reorderedZones: FloorPlanZone[]) => reorderZonesMutation.mutateAsync(reorderedZones),
    [reorderZonesMutation]
  );

  const setDefaultZone = useCallback(
    (zoneId: string | null) => setDefaultZoneMutation.mutateAsync(zoneId),
    [setDefaultZoneMutation]
  );

  const updateZoneLayout = useCallback(
    (zoneId: string, row: number, col: number) =>
      updateZoneLayoutMutation.mutateAsync({ id: zoneId, row, col }),
    [updateZoneLayoutMutation]
  );

  const defaultZone = zones.find(z => z.is_default) || null;

  return {
    zones,
    defaultZone,
    isLoading,
    error,
    createZone,
    updateZone,
    deleteZone,
    reorderZones,
    setDefaultZone,
    updateZoneLayout,
    isCreating: createZoneMutation.isPending,
    isUpdating: updateZoneMutation.isPending,
    isDeleting: deleteZoneMutation.isPending,
    isReordering: reorderZonesMutation.isPending,
    isSettingDefault: setDefaultZoneMutation.isPending,
  };
};
